import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listPurchaseFaults, type PurchaseFault } from './api';
import { publishInvitation } from '@/src/infrastructure/lib/api';

/** "17h 42m" until the deadline; null once it has passed. */
export function formatCountdown(deadline: Date, now: Date): string | null {
  const ms = deadline.getTime() - now.getTime();
  if (ms <= 0) return null;
  const mins = Math.floor(ms / 60_000);
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}h ${mins % 60}m` : `${mins}m`;
}

/**
 * D-19 — shown when our service detected (or support confirmed) a problem with a paid invitation. Tells the
 * customer what happened, lets them retry or reach support, and counts down the 24 h refund window.
 * Support has already been alerted automatically (a ticket is opened for system-detected faults).
 */
export function PurchaseFaultBanner() {
  const [faults, setFaults] = useState<PurchaseFault[]>([]);
  const [now, setNow] = useState(() => new Date());
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Re-check on every tick, so the banner disappears by itself once the problem is fixed (published on a
  // retry, fixed by the team, or refunded) — no page reload needed.
  const refresh = useCallback(() => {
    listPurchaseFaults()
      .then((f) => setFaults(f))
      .catch(() => undefined); // a banner must never break the dashboard
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(() => {
      setNow(new Date());
      refresh();
    }, 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  async function retry(f: PurchaseFault) {
    if (!f.invitationId) return;
    setBusy(f.purchaseId);
    setNote(null);
    try {
      await publishInvitation(f.invitationId);
      setNote('Publishing again — this message will disappear as soon as it works.');
      setTimeout(refresh, 5_000);
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Could not retry publishing. Please contact support.');
    } finally {
      setBusy(null);
    }
  }

  const open = faults
    .map((f) => ({ f, left: formatCountdown(new Date(f.deadline), now) }))
    .filter((x): x is { f: PurchaseFault; left: string } => x.left !== null);
  if (open.length === 0) return null;

  return (
    <div className="mb-8 space-y-3" role="region" aria-label="Invitation problem">
      {open.map(({ f, left }) => (
        <div key={f.purchaseId} className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5">
          <p className="font-semibold">We found a problem with {f.eventName ?? 'your invitation'}</p>
          <p className="text-sm opacity-80 mt-1">
            {f.source === 'ADMIN' ? 'Our team confirmed it.' : 'It failed to publish, even after an automatic retry.'} Our support team has
            been alerted and will check it. They will fix it or re-deliver it, or help you with a refund.
          </p>
          <p className="text-sm mt-2">
            <strong>Refund available for {left}</strong>
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {f.invitationId && (
              <button
                onClick={() => void retry(f)}
                disabled={busy === f.purchaseId}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-current disabled:opacity-50"
              >
                {busy === f.purchaseId ? 'Retrying…' : 'Retry publishing'}
              </button>
            )}
            <Link
              to={f.supportTicketId ? `/dashboard/help/tickets/${f.supportTicketId}` : '/dashboard/help/tickets/new'}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-current"
            >
              {f.supportTicketId ? 'View support ticket' : 'Contact support'}
            </Link>
            <Link to="/dashboard/settings?section=refunds" className="px-4 py-2 rounded-lg text-sm font-semibold border border-current">
              Request a refund
            </Link>
          </div>
        </div>
      ))}
      {note && <p className="text-sm">{note}</p>}
    </div>
  );
}
